import React, { useState, useEffect, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { 
  Plus, Trash2, Calendar, Clock, Users, Mail, Bell, 
  Loader2, ChevronDown, ChevronUp, Pencil, Check, X,
  AlertTriangle, Repeat, CalendarDays
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { eventosAPI, contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Opciones por defecto
const CALENDARIOS = [
  { value: "juicio", label: "Juicio", color: "#EF4444" },
  { value: "audiencia_previa", label: "Audiencia Previa", color: "#F59E0B" },
  { value: "conciliacion", label: "Conciliación", color: "#10B981" },
  { value: "reunion", label: "Reunión", color: "#3B82F6" },
  { value: "plazo", label: "Plazo", color: "#8B5CF6" },
];

const TIPOS_EVENTO = [
  { value: "cita", label: "Cita" },
  { value: "vencimiento", label: "Vencimiento" },
  { value: "recordatorio", label: "Recordatorio" },
  { value: "tramite", label: "Trámite" },
  { value: "otro", label: "Otro" },
];

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "bg-yellow-100 text-yellow-800" },
  { value: "completada", label: "Completada", color: "bg-green-100 text-green-800" },
  { value: "pospuesta", label: "Pospuesta", color: "bg-blue-100 text-blue-800" },
  { value: "cancelada", label: "Cancelada", color: "bg-red-100 text-red-800" },
];

const ALERTAS_OPCIONES = [
  { value: "5min", label: "5 minutos antes" },
  { value: "15min", label: "15 minutos antes" },
  { value: "30min", label: "30 minutos antes" },
  { value: "1hora", label: "1 hora antes" },
  { value: "2horas", label: "2 horas antes" },
  { value: "1dia", label: "1 día antes" },
  { value: "1semana", label: "1 semana antes" },
  { value: "1mes", label: "1 mes antes" },
];

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('es-ES', { 
    dateStyle: 'short', 
    timeStyle: 'short' 
  });
};

const getCalendarioColor = (calendario) => {
  const cal = CALENDARIOS.find(c => c.value === calendario);
  return cal?.color || '#6B7280';
};

const getEstadoBadge = (estado) => {
  const est = ESTADOS.find(e => e.value === estado);
  return est?.color || 'bg-gray-100 text-gray-800';
};

// Componente principal
const EventosSection = ({ expedienteId, onRefresh }) => {
  const [eventos, setEventos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedEventos, setExpandedEventos] = useState(new Set());
  
  // Contactos para participantes
  const [contactos, setContactos] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    titulo: '',
    calendario: 'juicio',
    tipo: '',
    estado: 'pendiente',
    todo_el_dia: false,
    repetir: false,
    fecha_inicio: '',
    hora_inicio: '',
    fecha_fin: '',
    hora_fin: '',
    alertas: [],
    asignados: '',
    participantes_ids: [],
    emails_externos: '',
    descripcion: ''
  });
  
  // Error de validación
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    loadEventos();
    loadContactos();
  }, [expedienteId]);

  const loadEventos = async () => {
    try {
      setLoading(true);
      const response = await eventosAPI.getByExpediente(expedienteId);
      setEventos(response.data);
    } catch (error) {
      console.error('Error cargando eventos:', error);
      toast({ title: "Error", description: "No se pudieron cargar los eventos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadContactos = async () => {
    try {
      const response = await contactosAPI.getAll();
      setContactos(response.data.filter(c => c.tipo !== 'organismo'));
    } catch (error) {
      console.error('Error cargando contactos:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      calendario: 'juicio',
      tipo: '',
      estado: 'pendiente',
      todo_el_dia: false,
      repetir: false,
      fecha_inicio: new Date().toISOString().split('T')[0],
      hora_inicio: '09:00',
      fecha_fin: '',
      hora_fin: '',
      alertas: [],
      asignados: '',
      participantes_ids: [],
      emails_externos: '',
      descripcion: ''
    });
    setDateError('');
  };

  const validateDates = () => {
    if (formData.fecha_fin && formData.fecha_inicio) {
      const inicio = new Date(`${formData.fecha_inicio}T${formData.hora_inicio || '00:00'}`);
      const fin = new Date(`${formData.fecha_fin}T${formData.hora_fin || '23:59'}`);
      if (inicio > fin) {
        setDateError('La fecha de comienzo no puede ser posterior a la de fin.');
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateDates()) return;
    
    setSaving(true);
    
    try {
      const fechaInicio = formData.todo_el_dia 
        ? `${formData.fecha_inicio}T00:00:00`
        : `${formData.fecha_inicio}T${formData.hora_inicio || '00:00'}:00`;
      
      let fechaFin = null;
      if (formData.fecha_fin) {
        fechaFin = formData.todo_el_dia 
          ? `${formData.fecha_fin}T23:59:59`
          : `${formData.fecha_fin}T${formData.hora_fin || '23:59'}:00`;
      }
      
      const data = {
        titulo: formData.titulo,
        calendario: formData.calendario,
        tipo: formData.tipo || null,
        estado: formData.estado,
        todo_el_dia: formData.todo_el_dia,
        repetir: formData.repetir,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        alertas: formData.alertas,
        asignados: formData.asignados || null,
        participantes_ids: formData.participantes_ids,
        emails_externos: formData.emails_externos || null,
        descripcion: formData.descripcion || null
      };
      
      await eventosAPI.createForExpediente(expedienteId, data);
      toast({ title: "Evento creado" });
      setIsDialogOpen(false);
      resetForm();
      loadEventos();
    } catch (error) {
      console.error('Error creando evento:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo crear el evento", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (evento, e) => {
    e?.stopPropagation();
    setEditingEvento(evento);
    
    const fechaInicio = evento.fecha_inicio ? new Date(evento.fecha_inicio) : null;
    const fechaFin = evento.fecha_fin ? new Date(evento.fecha_fin) : null;
    
    setFormData({
      titulo: evento.titulo || '',
      calendario: evento.calendario || 'juicio',
      tipo: evento.tipo || '',
      estado: evento.estado || 'pendiente',
      todo_el_dia: evento.todo_el_dia || false,
      repetir: evento.repetir || false,
      fecha_inicio: fechaInicio ? fechaInicio.toISOString().split('T')[0] : '',
      hora_inicio: fechaInicio ? fechaInicio.toTimeString().slice(0, 5) : '',
      fecha_fin: fechaFin ? fechaFin.toISOString().split('T')[0] : '',
      hora_fin: fechaFin ? fechaFin.toTimeString().slice(0, 5) : '',
      alertas: evento.alertas || [],
      asignados: evento.asignados || '',
      participantes_ids: evento.participantes_ids || [],
      emails_externos: evento.emails_externos || '',
      descripcion: evento.descripcion || ''
    });
    setDateError('');
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateDates()) return;
    
    setSaving(true);
    
    try {
      const fechaInicio = formData.todo_el_dia 
        ? `${formData.fecha_inicio}T00:00:00`
        : `${formData.fecha_inicio}T${formData.hora_inicio || '00:00'}:00`;
      
      let fechaFin = null;
      if (formData.fecha_fin) {
        fechaFin = formData.todo_el_dia 
          ? `${formData.fecha_fin}T23:59:59`
          : `${formData.fecha_fin}T${formData.hora_fin || '23:59'}:00`;
      }
      
      const data = {
        titulo: formData.titulo,
        calendario: formData.calendario,
        tipo: formData.tipo || null,
        estado: formData.estado,
        todo_el_dia: formData.todo_el_dia,
        repetir: formData.repetir,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        alertas: formData.alertas,
        asignados: formData.asignados || null,
        participantes_ids: formData.participantes_ids,
        emails_externos: formData.emails_externos || null,
        descripcion: formData.descripcion || null
      };
      
      await eventosAPI.update(editingEvento.id, data);
      toast({ title: "Evento actualizado" });
      setIsEditDialogOpen(false);
      setEditingEvento(null);
      resetForm();
      loadEventos();
    } catch (error) {
      console.error('Error actualizando evento:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo actualizar el evento", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (evento, e) => {
    e?.stopPropagation();
    if (!window.confirm(`¿Eliminar el evento "${evento.titulo}"?`)) return;
    
    try {
      await eventosAPI.delete(evento.id);
      toast({ title: "Evento eliminado" });
      loadEventos();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el evento", variant: "destructive" });
    }
  };

  const toggleEvento = (id) => {
    setExpandedEventos(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleAlertaToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      alertas: prev.alertas.includes(value)
        ? prev.alertas.filter(a => a !== value)
        : [...prev.alertas, value]
    }));
  };

  const handleParticipanteToggle = (id) => {
    setFormData(prev => ({
      ...prev,
      participantes_ids: prev.participantes_ids.includes(id)
        ? prev.participantes_ids.filter(p => p !== id)
        : [...prev.participantes_ids, id]
    }));
  };

  // Render del formulario (compartido entre crear y editar)
  const renderForm = (onSubmit, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Título */}
      <div>
        <Label>Título <span className="text-red-500">*</span></Label>
        <div className="flex items-center justify-between mt-1">
          <Input 
            value={formData.titulo}
            onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
            placeholder="Obligatorio"
            maxLength={150}
            required 
            className="flex-1"
          />
          <span className="text-xs text-gray-400 ml-2">{formData.titulo.length} / 150</span>
        </div>
      </div>

      {/* Calendario y Tipo */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-gray-500">Comience a escribir para buscar</Label>
          <Select 
            value={formData.calendario} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, calendario: v }))}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Calendario" /></SelectTrigger>
            <SelectContent>
              {CALENDARIOS.map(cal => (
                <SelectItem key={cal.value} value={cal.value}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cal.color }} />
                    {cal.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Comience a escribir para buscar</Label>
          <Select 
            value={formData.tipo} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, tipo: v }))}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              {TIPOS_EVENTO.map(tipo => (
                <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Todo el día, Repetir, Estado */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch 
            checked={formData.todo_el_dia}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, todo_el_dia: checked }))}
          />
          <Label className="text-sm">Todo el día</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch 
            checked={formData.repetir}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, repetir: checked }))}
          />
          <Label className="text-sm">Repetir</Label>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={formData.estado} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, estado: v }))}
          >
            <SelectTrigger className="w-[150px]">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <SelectValue placeholder="Estado" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {ESTADOS.map(est => (
                <SelectItem key={est.value} value={est.value}>{est.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Validación de fechas */}
      {dateError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-600 text-sm">{dateError}</span>
        </div>
      )}

      {/* Fechas */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> Fecha de comienzo <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2 mt-1">
            <Input 
              type="date" 
              value={formData.fecha_inicio}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
              required
              className="flex-1"
            />
            {!formData.todo_el_dia && (
              <Input 
                type="time" 
                value={formData.hora_inicio}
                onChange={(e) => setFormData(prev => ({ ...prev, hora_inicio: e.target.value }))}
                className="w-28"
              />
            )}
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> Fecha de fin
          </Label>
          <div className="flex gap-2 mt-1">
            <Input 
              type="date" 
              value={formData.fecha_fin}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_fin: e.target.value }))}
              className="flex-1"
            />
            {!formData.todo_el_dia && (
              <Input 
                type="time" 
                value={formData.hora_fin}
                onChange={(e) => setFormData(prev => ({ ...prev, hora_fin: e.target.value }))}
                className="w-28"
              />
            )}
          </div>
        </div>
      </div>

      {/* Alertas */}
      <div>
        <Label className="flex items-center gap-1 mb-2">
          <Bell className="w-4 h-4" /> Alertas
        </Label>
        <Select>
          <SelectTrigger>
            <SelectValue placeholder={formData.alertas.length > 0 ? `${formData.alertas.length} alerta(s) seleccionada(s)` : "Alertas"} />
          </SelectTrigger>
          <SelectContent>
            {ALERTAS_OPCIONES.map(alerta => (
              <div 
                key={alerta.value} 
                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleAlertaToggle(alerta.value)}
              >
                <Checkbox checked={formData.alertas.includes(alerta.value)} />
                <span>{alerta.label}</span>
              </div>
            ))}
          </SelectContent>
        </Select>
        {formData.alertas.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.alertas.map(a => (
              <Badge key={a} variant="secondary" className="text-xs">
                {ALERTAS_OPCIONES.find(o => o.value === a)?.label}
                <button 
                  type="button" 
                  className="ml-1" 
                  onClick={() => handleAlertaToggle(a)}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Personas asignadas */}
      <div>
        <Label className="flex items-center gap-1 mb-1">
          <Users className="w-4 h-4" /> Personas asignadas
        </Label>
        <Input 
          value={formData.asignados}
          onChange={(e) => setFormData(prev => ({ ...prev, asignados: e.target.value }))}
          placeholder="Escriba los nombres de las personas asignadas"
        />
      </div>

      {/* Participantes */}
      <div>
        <Label className="text-xs text-gray-500">Comience a escribir para buscar</Label>
        <Select>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={formData.participantes_ids.length > 0 ? `${formData.participantes_ids.length} participante(s)` : "Participantes"} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {contactos.map(contacto => (
              <div 
                key={contacto.id} 
                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleParticipanteToggle(contacto.id)}
              >
                <Checkbox checked={formData.participantes_ids.includes(contacto.id)} />
                <span>{contacto.nombre}</span>
                {contacto.email && <span className="text-xs text-gray-400">({contacto.email})</span>}
              </div>
            ))}
          </SelectContent>
        </Select>
        {formData.participantes_ids.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.participantes_ids.map(id => {
              const contacto = contactos.find(c => c.id === id);
              return contacto && (
                <Badge key={id} variant="secondary" className="text-xs">
                  {contacto.nombre}
                  <button 
                    type="button" 
                    className="ml-1" 
                    onClick={() => handleParticipanteToggle(id)}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      {/* Emails externos */}
      <div>
        <Label>Emails de participantes externos</Label>
        <Input 
          value={formData.emails_externos}
          onChange={(e) => setFormData(prev => ({ ...prev, emails_externos: e.target.value }))}
          placeholder="Separados por , (coma)"
          className="mt-1"
        />
      </div>

      {/* Descripción */}
      <div data-color-mode="light">
        <Label className="mb-1 block">Descripción</Label>
        <MDEditor
          value={formData.descripcion}
          onChange={(val) => setFormData(prev => ({ ...prev, descripcion: val || '' }))}
          preview="edit"
          height={150}
          textareaProps={{ placeholder: 'Descripción' }}
        />
      </div>

      <DialogFooter className="pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            isEdit ? setIsEditDialogOpen(false) : setIsDialogOpen(false);
            resetForm();
          }}
        >
          Cancelar
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {isEdit ? 'Guardar cambios' : 'Guardar'}
        </Button>
      </DialogFooter>
    </form>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        {/* Dialog Nueva */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800">
              <Plus className="w-4 h-4 mr-2" />NUEVO EVENTO
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo evento</DialogTitle>
            </DialogHeader>
            {renderForm(handleSubmit, false)}
          </DialogContent>
        </Dialog>

        {/* Dialog Editar */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) { setEditingEvento(null); resetForm(); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar evento</DialogTitle>
            </DialogHeader>
            {editingEvento && renderForm(handleEditSubmit, true)}
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista de eventos */}
      <div className="space-y-3">
        {eventos.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No hay eventos</p>
        ) : (
          eventos.map((evento) => {
            const isExpanded = expandedEventos.has(evento.id);
            const calendarioColor = getCalendarioColor(evento.calendario);
            
            return (
              <div key={evento.id} className="border rounded-lg overflow-hidden">
                <div 
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleEvento(evento.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div 
                        className="w-3 h-full min-h-[40px] rounded" 
                        style={{ backgroundColor: calendarioColor }}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          {evento.titulo}
                          <Badge className={getEstadoBadge(evento.estado)}>
                            {ESTADOS.find(e => e.value === evento.estado)?.label || evento.estado}
                          </Badge>
                          {evento.alertas?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Bell className="w-3 h-3 mr-1" />{evento.alertas.length}
                            </Badge>
                          )}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(evento.fecha_inicio)}
                          </span>
                          {evento.fecha_fin && (
                            <span className="flex items-center gap-1">
                              → {formatDateTime(evento.fecha_fin)}
                            </span>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {CALENDARIOS.find(c => c.value === evento.calendario)?.label || evento.calendario}
                          </Badge>
                          {evento.todo_el_dia && (
                            <Badge variant="outline" className="text-xs">
                              <CalendarDays className="w-3 h-3 mr-1" />Todo el día
                            </Badge>
                          )}
                          {evento.repetir && (
                            <Badge variant="outline" className="text-xs">
                              <Repeat className="w-3 h-3 mr-1" />Repetir
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleEditClick(evento, e)}
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleDelete(evento, e)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    {/* Descripción */}
                    {evento.descripcion && (
                      <div className="mt-3" data-color-mode="light">
                        <p className="text-sm font-medium text-gray-700 mb-2">Descripción:</p>
                        <div className="bg-white p-3 rounded border prose prose-sm max-w-none">
                          <MDEditor.Markdown source={evento.descripcion} />
                        </div>
                      </div>
                    )}
                    
                    {/* Detalles adicionales */}
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      {evento.asignados && (
                        <div>
                          <span className="text-gray-500">Asignados:</span>
                          <span className="ml-2">{evento.asignados}</span>
                        </div>
                      )}
                      {evento.participantes?.length > 0 && (
                        <div>
                          <span className="text-gray-500">Participantes:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {evento.participantes.map(p => (
                              <Badge key={p.id} variant="secondary" className="text-xs">{p.nombre}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {evento.emails_externos && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Emails externos:</span>
                          <span className="ml-2">{evento.emails_externos}</span>
                        </div>
                      )}
                      {evento.alertas?.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Alertas:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {evento.alertas.map(a => (
                              <Badge key={a} variant="outline" className="text-xs">
                                <Bell className="w-3 h-3 mr-1" />
                                {ALERTAS_OPCIONES.find(o => o.value === a)?.label || a}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EventosSection;
